import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, ShieldCheck, Users, FlaskConical, ShoppingCart } from "lucide-react";
import { useAuth } from "@/auth";
import { useCart } from "@/cart";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = { essential: "Essential", advanced: "Advanced", pro: "Pro" };

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const { addItem } = useCart();
  const location = useLocation();
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    })();
  }, [location.state]);

  const load = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUsers(data.users);
    } catch {
      setUsers([]);
    }
  };

  useEffect(() => {
    if (authed === true && user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, user]);

  const act = async (key, path, body, successMsg) => {
    setBusy(key);
    try {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Action failed");
      toast.success(successMsg);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  if (authed === null || authLoading) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-32">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </main>
    );
  }

  if (authed === false) {
    return (
      <main data-testid="admin-signed-out" className="flex min-h-[70vh] items-center pt-32 pb-24">
      <Seo title="Admin — DISJINX" description="DISJINX owner admin." path="/admin" noindex />
        <div className="mx-auto max-w-lg px-6 text-center">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Owner sign-in required</h1>
          <p className="mt-6 text-base text-neutral-600">This page is for the DISJINX owner. Sign in with the admin Google account to continue.</p>
          <Link
            to="/signin"
            data-testid="admin-signin-button"
            className="mt-10 inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
          >
            Sign in with Google
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    );
  }

  if (!user?.is_admin) {
    return (
      <main data-testid="admin-not-authorized" className="flex min-h-[70vh] items-center pt-32 pb-24">
      <Seo title="Admin — DISJINX" description="DISJINX owner admin." path="/admin" noindex />
        <div className="mx-auto max-w-lg px-6 text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-neutral-300" strokeWidth={1.5} />
          <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight">Owner access only</h1>
          <p className="mt-6 text-base text-neutral-600">
            You're signed in as {user?.email}, which isn't the DISJINX admin account.
          </p>
          <Link to="/account" data-testid="admin-back-to-account" className="mt-10 inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800">
            Go to My Billing
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="pt-40 pb-32 lg:pt-48">
      <Seo title="Admin — DISJINX" description="DISJINX owner admin." path="/admin" noindex />
      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">Owner admin</p>
          <h1 className="font-display text-4xl font-extrabold tracking-tight lg:text-5xl">Customer plans</h1>
          <p className="mt-4 max-w-xl text-base text-neutral-600">
            Assign a plan once a customer's automation is agreed. They'll see it on their My Billing page, where they can cancel at period end.
          </p>

          <div data-testid="admin-test-plan" className="mt-12 border border-dashed border-amber-300 bg-amber-50/50 p-6 lg:p-8">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="max-w-xl">
                <div className="flex items-center gap-2">
                  <FlaskConical className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700">Checkout testing</p>
                </div>
                <p className="mt-3 font-display text-lg font-bold tracking-tight">Test Plan — hidden from the public site</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">
                  $0.50 setup fee charged at checkout · $1/week subscription (starts at $0, bill manually when ready) · $0.001/credit usage rate.
                  Only you can see and purchase this plan — use it to run a live card checkout end-to-end.
                </p>
              </div>
              <div className="flex flex-col items-start gap-3">
                <button
                  data-testid="admin-test-plan-add"
                  onClick={() => addItem("test")}
                  className="inline-flex items-center gap-2 bg-neutral-950 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
                >
                  <ShoppingCart className="h-4 w-4" />
                  Add Test Plan to cart
                </button>
                <button
                  data-testid="admin-stripe-sync"
                  disabled={busy === "stripe-sync"}
                  onClick={async () => {
                    setBusy("stripe-sync");
                    try {
                      const res = await fetch(`${API}/admin/stripe-sync`, { method: "POST", credentials: "include" });
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.detail || "Sync failed");
                      toast.success(`Stripe plans synced: ${data.synced.join(", ")}`);
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setBusy(null);
                    }
                  }}
                  className="inline-flex items-center gap-2 border border-neutral-300 px-6 py-3 text-sm font-medium text-neutral-700 transition-colors duration-200 hover:border-neutral-950 hover:text-neutral-950 disabled:opacity-50"
                >
                  {busy === "stripe-sync" && <Loader2 className="h-4 w-4 animate-spin" />}
                  Sync plans to Stripe
                </button>
              </div>
            </div>
          </div>

          {users === null ? (
            <div className="mt-20 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
            </div>
          ) : users.length === 0 ? (
            <div data-testid="admin-empty" className="mt-16 border border-neutral-200 bg-neutral-50 p-10">
              <Users className="h-6 w-6 text-neutral-400" strokeWidth={1.5} />
              <p className="mt-4 text-sm text-neutral-600">No customer accounts yet. Accounts appear here after someone signs in with Google.</p>
            </div>
          ) : (
            <div className="mt-12 space-y-6" data-testid="admin-users-list">
              {users.map((u) => (
                <div key={u.user_id} data-testid={`admin-user-${u.email}`} className="border border-neutral-200 bg-white p-6 lg:p-8">
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="min-w-0">
                      <p className="font-display text-lg font-bold tracking-tight">{u.name || "—"}</p>
                      <p className="mt-1 text-sm text-neutral-500">{u.email}</p>
                      {u.latest_plan_enquiry && (
                        <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-600">
                          <span className="mr-2 inline-block border border-neutral-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-500">
                            {PLAN_LABELS[u.latest_plan_enquiry.kind.replace("plan_", "")] || "Plan"} enquiry
                          </span>
                          {u.latest_plan_enquiry.message.length > 140
                            ? u.latest_plan_enquiry.message.slice(0, 140) + "…"
                            : u.latest_plan_enquiry.message}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      {u.active_plan ? (
                        <span data-testid={`admin-plan-badge-${u.email}`} className="border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                          {u.active_plan.plan_label} · ${u.active_plan.weekly_price}/wk
                          {u.active_plan.cancel_at_period_end ? " · cancels at period end" : ""}
                        </span>
                      ) : (
                        <span className="border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-500">
                          No plan
                        </span>
                      )}
                      <div className="flex flex-wrap justify-end gap-2">
                        {["essential", "advanced", "pro"].map((p) => (
                          <button
                            key={p}
                            data-testid={`admin-assign-${p}-${u.email}`}
                            disabled={busy !== null}
                            onClick={() => act(`${p}-${u.email}`, "/admin/assign-plan", { email: u.email, plan: p }, `${PLAN_LABELS[p]} assigned to ${u.email}`)}
                            className="border border-neutral-300 px-3 py-2 text-xs font-semibold transition-colors duration-200 hover:border-neutral-950 disabled:opacity-40"
                          >
                            {busy === `${p}-${u.email}` ? "…" : PLAN_LABELS[p]}
                          </button>
                        ))}
                        {u.active_plan && (
                          <button
                            data-testid={`admin-revoke-${u.email}`}
                            disabled={busy !== null}
                            onClick={() => act(`revoke-${u.email}`, "/admin/revoke-plan", { email: u.email }, `Plan revoked for ${u.email}`)}
                            className="border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition-colors duration-200 hover:border-red-500 disabled:opacity-40"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
}
